import React from 'react';
import {
  Check, X as XIcon, MessageSquare, Eye, RotateCcw, Send, Clock, ShieldCheck,
} from 'lucide-react';

/**
 * EVENT_META — mapping from event.type → icon + ring color
 * Used by both <AuditTimeline/> (per-request) and <AuditFeed/> (company-wide).
 */
const EVENT_META = {
  submitted:     { Icon: Send,         ring: 'bg-slate-400',    label: 'submitted' },
  viewed:        { Icon: Eye,          ring: 'bg-emerald-400',  label: 'viewed' },
  comment:       { Icon: MessageSquare, ring: 'bg-blue-400',     label: 'commented' },
  returned:      { Icon: RotateCcw,    ring: 'bg-orange-500',   label: 'returned for revision' },
  step_approved: { Icon: ShieldCheck,  ring: 'bg-indigo-500',   label: 'approved a step' },
  approved:      { Icon: Check,        ring: 'bg-green-500',    label: 'approved' },
  rejected:      { Icon: XIcon,        ring: 'bg-red-500',      label: 'rejected' },
};

const KIND_LABEL = { correction: 'Payroll correction', leave: 'Leave request', advance: 'Cash advance' };

const fmtAbs = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
};

/**
 * AuditTimeline — vertical timeline for ONE request.
 * Props:
 *  - events: AuditEvent[]
 *  - loading?: boolean
 */
export const AuditTimeline = ({ events = [], loading = false }) => {
  if (loading) {
    return (
      <div data-testid="audit-timeline-loading" className="text-xs text-slate-500 px-4 py-3">
        Loading audit log…
      </div>
    );
  }
  if (!events.length) {
    return (
      <div data-testid="audit-timeline-empty" className="text-xs text-slate-500 italic px-4 py-3 flex items-center gap-1.5">
        <Clock className="w-3 h-3" /> No audit events yet.
      </div>
    );
  }
  return (
    <ol data-testid="audit-timeline" className="relative pl-7 py-3 space-y-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {events.map((e, i) => {
        const meta = EVENT_META[e.type] || EVENT_META.comment;
        const Icon = meta.Icon;
        return (
          <li key={`${e.type}-${e.at}-${i}`} data-testid={`audit-event-${e.type}`} className="relative">
            {/* vertical line */}
            {i !== events.length - 1 && (
              <span className="absolute left-[-18px] top-6 bottom-[-12px] w-px bg-slate-200" aria-hidden />
            )}
            {/* dot */}
            <span className={`absolute left-[-26px] top-1 inline-flex items-center justify-center w-5 h-5 rounded-full ${meta.ring} text-white shadow-sm`}>
              <Icon className="w-3 h-3" strokeWidth={2.5} />
            </span>
            <div className="text-sm">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="font-semibold text-slate-900">{e.actor_name || 'Someone'}</span>
                <span className="text-slate-500">{meta.label}</span>
                <span className="text-[11px] text-slate-400 ml-auto" title={fmtAbs(e.at)}>{fmtRel(e.at)}</span>
              </div>
              {e.body && (
                <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap leading-snug bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
                  {e.body}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

/**
 * AuditFeed — company-wide stream, grouped by date.
 * Props:
 *  - events: AuditEvent[]
 *  - onOpenRequest?: ({ kind, request_id }) => void   // click to drill into a request
 */
export const AuditFeed = ({ events = [], onOpenRequest }) => {
  if (!events.length) {
    return (
      <div data-testid="audit-feed-empty" className="text-sm text-slate-500 italic py-8 text-center">
        No audit events in this filter.
      </div>
    );
  }
  // Group by YYYY-MM-DD
  const groups = {};
  for (const e of events) {
    const dayKey = (e.at || '').slice(0, 10) || 'unknown';
    (groups[dayKey] = groups[dayKey] || []).push(e);
  }
  const days = Object.keys(groups).sort().reverse();

  return (
    <div data-testid="audit-feed" className="space-y-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {days.map((day) => (
        <section key={day}>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">
            {day === 'unknown' ? 'Unknown date' : new Date(day).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
          </div>
          <ul className="space-y-1.5">
            {groups[day].map((e, i) => {
              const meta = EVENT_META[e.type] || EVENT_META.comment;
              const Icon = meta.Icon;
              return (
                <li
                  key={`${e.type}-${e.at}-${i}`}
                  data-testid={`audit-feed-row-${e.type}`}
                  onClick={onOpenRequest ? () => onOpenRequest({ kind: e.request_kind, request_id: e.request_id }) : undefined}
                  className={`flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-md transition-colors ${onOpenRequest ? 'hover:border-[#002FA7] cursor-pointer' : ''}`}
                >
                  <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full ${meta.ring} text-white`}>
                    <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-900">{e.actor_name || 'Someone'}</span>
                      <span className="text-slate-500"> {meta.label} </span>
                      <span className="text-slate-700">{KIND_LABEL[e.request_kind] || e.request_kind}</span>
                    </div>
                    {e.body && (
                      <p className="text-xs text-slate-600 mt-0.5 truncate">{e.body}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-[11px] text-slate-400 text-right" title={fmtAbs(e.at)}>
                    {fmtRel(e.at)}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

/**
 * Tiny inline shield to brand "Audit Trail" headers.
 */
export const AuditHeader = ({ children }) => (
  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">
    <ShieldCheck className="w-3.5 h-3.5 text-[#002FA7]" /> {children}
  </div>
);

export default AuditTimeline;
